import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MapPin, ExternalLink, Award, BadgeCheck, Heart } from "lucide-react";
import { logError } from "@/lib/log";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Breeders() {
  usePageMeta("Find a Verified Dog Breeder in Australia | Fetchright", "Official ANKC-affiliated breeder registries for every Australian state — code-of-ethics bound, health-tested and traceable.");
  const [postcode, setPostcode] = useState("");
  const [data, setData] = useState({ sponsored: [], registries: [], state: null });

  useEffect(() => {
    const pc = postcode.trim();
    if (pc && pc.length !== 4) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/monetization/breeders`, { params: pc ? { postcode: pc } : {} });
        setData(r.data);
      } catch (error) {
        logError("Failed to load breeder directory:", error);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [postcode]);

  const state = data.state;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-8">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Ethical breeders</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
          Find a verified breeder
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          If you want a particular breed from a puppy, the safest path is an{" "}
          <span className="font-semibold text-accent">ANKC-affiliated ethical breeder</span> — code-of-ethics
          bound, health-tested, and traceable. Every registry below is an official state or national kennel body.
        </p>
      </div>

      <div className="relative max-w-xs mb-10">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="breeders-postcode-input"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="Postcode — highlight your state"
          className="pl-9 rounded-full border-border/70"
          inputMode="numeric"
        />
      </div>

      {data.sponsored.length > 0 && (
        <div className="grid md:grid-cols-2 gap-5 mb-8">
          {data.sponsored.map((b, i) => (
            <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer sponsored"
               data-testid={`sponsored-breeder-${i}`}
               className="group rounded-3xl border-2 border-primary bg-card p-6 warm-shadow hover:-translate-y-1 transition-transform">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground bg-primary rounded-full px-2 py-0.5 mb-2">
                Featured Partner
              </span>
              {b.verified && (
                <span
                  data-testid={`verified-breeder-badge-${i}`}
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5 mb-2"
                >
                  <BadgeCheck size={11} /> Verified Breeder
                </span>
              )}
              <h3 className="font-display font-bold text-xl text-accent">{b.name}</h3>
              <p className="text-sm text-accent/80 mt-2">{b.description}</p>
              <div className="mt-4 text-primary font-semibold text-sm inline-flex items-center gap-1">
                View breeder <ExternalLink size={12} />
              </div>
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Award size={20} className="text-primary" />
        <h2 className="font-display font-bold text-2xl text-accent">Official breeder registries</h2>
        <span className="text-sm text-muted-foreground">({data.registries.length})</span>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {data.registries.map((r, i) => (
          <a
            key={r.name}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`breeder-registry-${i}`}
            className={`group rounded-3xl border border-border/60 warm-shadow p-6 hover:-translate-y-1 transition-transform bg-card ${
              r.state === state && state ? "ring-2 ring-primary/40" : ""
            }`}
          >
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                r.state === "national" ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"
              }`}>
                {r.state === "national" ? "National" : r.state}
              </span>
              {r.verified && (
                <span
                  data-testid={`verified-registry-badge-${i}`}
                  title="Official ANKC-affiliated / national kennel body"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800"
                >
                  <BadgeCheck size={11} /> Verified
                </span>
              )}
              {r.state === state && state && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-primary">Near you</span>
              )}
            </div>
            <h3 className="font-display font-bold text-lg text-accent">{r.name}</h3>
            <p className="text-xs text-accent/70 mt-2 leading-relaxed">{r.description}</p>
            <div className="mt-4 text-primary font-semibold text-sm inline-flex items-center gap-1">
              Find a breeder <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </div>
          </a>
        ))}
      </div>

      <p className="text-base text-muted-foreground mt-6">
        ⚠️ Avoid Gumtree, Facebook Marketplace, and puppy farms. Every ANKC-registered breeder above is bound by a
        formal Code of Ethics with health-testing requirements.
      </p>

      <div className="mt-12 rounded-3xl bg-secondary p-6 md:p-8 flex flex-wrap items-center gap-4">
        <span className="w-11 h-11 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
          <Heart size={18} />
        </span>
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold text-lg text-accent">Open to adopting instead?</div>
          <div className="text-sm text-muted-foreground">
            Rescues charge ≈ $350–$800 instead of a purchase price — browse adoptable dogs near you.
          </div>
        </div>
        <Link
          to="/adoption"
          data-testid="breeders-to-adopt-link"
          className="pill-btn inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold"
        >
          See adoptable dogs
        </Link>
      </div>
    </div>
  );
}
